import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import ActionsDropdown from 'components/partials/actions-dropdown';
import Tooltip from 'components/partials/tooltip/tooltip';

import { useRegimeSelection } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-selection-context';

import Icons from 'assets/icons';

interface RegimeRowProps {
  regime: { id: string | number; name: string };
  actions: unknown[];
  dragListeners?: Record<string, unknown>;
  dragAttributes?: Record<string, unknown>;
  hideActions?: boolean;
  customClass?: string;
}

const RegimeRow = ({
  regime,
  actions,
  dragListeners,
  dragAttributes,
  hideActions = false,
  customClass,
}: RegimeRowProps): React.JSX.Element => {
  const regimeNameRef = useRef<HTMLButtonElement | null>(null);
  const { selectedRegimeId, onSelectRegime } = useRegimeSelection() as {
    selectedRegimeId?: string | number | null;
    onSelectRegime?: (id: string | number) => void;
  };
  const isSelected = selectedRegimeId === regime.id;
  const [isRegimeNameTruncated, setIsRegimeNameTruncated] =
    useState<boolean>(false);

  useLayoutEffect(() => {
    const element = regimeNameRef.current;

    if (element) {
      setIsRegimeNameTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [regime.name]);

  return (
    <div
      className={classNames(
        'flex items-center gap-3 rounded-lg pl-4 pr-2 py-2',
        isSelected && 'bg-gray-100',
        customClass
      )}
      data-testid={`regime-row-${regime.id}`}
    >
      <div className="flex flex-1 items-center gap-2 pr-2 min-w-0">
        <button
          type="button"
          className="cursor-grab touch-none shrink-0"
          {...dragListeners}
          {...dragAttributes}
          aria-label="Drag to reorder"
        >
          <Icons.GripVertical customClass="size-4 text-gray-400" />
        </button>
        <Tooltip
          content={regime.name}
          enabled={isRegimeNameTruncated}
          position="top-start"
        >
          <button
            ref={regimeNameRef}
            type="button"
            className="text-sm text-gray-700 truncate max-w-36 text-left"
            onClick={() => onSelectRegime?.(regime.id)}
          >
            {regime.name}
          </button>
        </Tooltip>
      </div>
      {!hideActions && (
        <ActionsDropdown
          options={actions as never}
          variant="reserve-text"
          customClass="!h-auto !p-1"
        />
      )}
    </div>
  );
};

export default RegimeRow;
